import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  Building2,
  Users,
  Layers,
  Trash2,
  UserPlus,
  X,
  Pencil,
  Shield,
} from "lucide-react";
import { toast } from "@fox1ck-org/ui/toast";
import { useOrgUnit, useOrgUnitMembers, useUpdateOrgUnit, useDeleteOrgUnit, useAddOrgUnitMember, useRemoveOrgUnitMember, useAssignMemberAppRoles } from "@/hooks/use-org-units";
import { MemberRolesDialog } from "@/components/members/MemberRolesDialog";
import { useAppRoles } from "@/hooks/use-app-roles";
import { useOrganizations } from "@/hooks/use-organizations";
import { useIsPlatformAdmin } from "@/lib/auth";
import { OrgUnitTypeBadge } from "@/components/shared/OrgUnitTypeBadge";
import { RoleBadge } from "@/components/shared/RoleBadge";
import { Avatar, AvatarFallback, Badge, Button, Card, CardContent, CardHeader, CardTitle, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, Input, ScrollArea, Select, SelectContent, SelectItem, SelectTrigger, SelectValue, Separator, Skeleton, Tabs, TabsContent, TabsList, TabsTrigger } from "@fox1ck-org/ui";
import { KeycloakGroupPicker } from "@/components/shared/KeycloakGroupPicker";
import type { AppRoleBinding, OrgUnit, OrgUnitMember } from "@/types/org";
import { UserPicker } from "@/components/shared/UserPicker";

interface OrgUnitDetailProps {
  unitId: string;
}

export function OrgUnitDetail({ unitId }: OrgUnitDetailProps) {
  const navigate = useNavigate();
  const isPlatformAdmin = useIsPlatformAdmin();
  const { data: unit, isLoading } = useOrgUnit(unitId);
  const { data: membersData, isLoading: membersLoading } = useOrgUnitMembers(unitId);
  const { data: appRolesData } = useAppRoles();
  const { data: orgsData } = useOrganizations({ max: 100 });
  const updateUnit = useUpdateOrgUnit(unitId);
  const deleteUnit = useDeleteOrgUnit();
  const removeMember = useRemoveOrgUnitMember(unitId);

  const [showDelete, setShowDelete] = useState(false);
  const [showAddMember, setShowAddMember] = useState(false);
  const [rolesForMember, setRolesForMember] = useState<OrgUnitMember | null>(null);
  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState("");
  const [editSlug, setEditSlug] = useState("");
  const [editType, setEditType] = useState("team");
  const [editStatus, setEditStatus] = useState("active");
  const [editMaxMembers, setEditMaxMembers] = useState("");
  const [editGroupId, setEditGroupId] = useState<string | null>(null);
  const [editGroupPath, setEditGroupPath] = useState<string | null>(null);

  function startEditing() {
    if (!unit) return;
    setEditName(unit.name);
    setEditSlug(unit.slug);
    setEditType(unit.type);
    setEditStatus(unit.status);
    setEditMaxMembers(unit.maxMembers?.toString() ?? "");
    setEditGroupId(unit.keycloakGroupId);
    setEditGroupPath(unit.keycloakGroupPath);
    setEditing(true);
  }

  async function saveEdit() {
    try {
      await updateUnit.mutateAsync({
        name: editName,
        slug: editSlug,
        type: editType,
        status: editStatus,
        maxMembers: editMaxMembers ? parseInt(editMaxMembers) : null,
        keycloakGroupId: editGroupId,
        keycloakGroupPath: editGroupPath,
      });
      setEditing(false);
      toast.success("Подразделение обновлено");
    } catch {
      toast.error("Не удалось обновить подразделение");
    }
  }

  const members = membersData?.members ?? [];
  const orgs = orgsData?.organizations ?? [];
  const linkedOrg = unit?.keycloakOrgId ? orgs.find((o) => o.id === unit.keycloakOrgId) : null;
  const appBindings = (appRolesData?.items ?? []).filter((b) =>
    b.orgUnitTypes.includes(unit?.type ?? "")
  );

  async function handleDelete() {
    try {
      await deleteUnit.mutateAsync(unitId);
      toast.success("Подразделение удалено");
      navigate("/org-structure");
    } catch {
      toast.error("Не удалось удалить подразделение");
    }
  }

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-32 w-full rounded-lg" />
        <Skeleton className="h-9 w-64 rounded-lg" />
        <Skeleton className="h-64 w-full rounded-lg" />
      </div>
    );
  }

  if (!unit) {
    return (
      <div className="flex items-center justify-center h-full text-muted-foreground">
        Подразделение не найдено.
      </div>
    );
  }

  return (
    <ScrollArea className="h-full">
      <div className="space-y-4 pr-2">
        {/* Header card */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                  {unit.type === "company" ? (
                    <Building2 className="h-5 w-5 text-primary" />
                  ) : unit.type === "department" ? (
                    <Layers className="h-5 w-5 text-primary" />
                  ) : (
                    <Users className="h-5 w-5 text-primary" />
                  )}
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h2 className="text-xl font-bold text-foreground">{unit.name}</h2>
                    <OrgUnitTypeBadge type={unit.type} />
                  </div>
                  <p className="text-sm text-muted-foreground font-mono mt-0.5">{unit.slug}</p>
                </div>
              </div>
              {isPlatformAdmin && !editing && (
                <div className="flex items-center gap-2">
                  <Button
                    variant="secondary"
                    size="icon"
                    className="h-8 w-8"
                    onClick={startEditing}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="danger"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => setShowDelete(true)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </div>

            <Separator className="my-4" />

            {editing ? (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm text-muted-foreground mb-1">Название</label>
                    <Input value={editName} onChange={(e) => setEditName(e.target.value)} />
                  </div>
                  <div>
                    <label className="block text-sm text-muted-foreground mb-1">Код</label>
                    <Input value={editSlug} onChange={(e) => setEditSlug(e.target.value)} className="font-mono" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm text-muted-foreground mb-1">Тип</label>
                    <Select value={editType} onValueChange={setEditType}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="company">Компания</SelectItem>
                        <SelectItem value="department">Отдел</SelectItem>
                        <SelectItem value="team">Команда</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="block text-sm text-muted-foreground mb-1">Статус</label>
                    <Select value={editStatus} onValueChange={setEditStatus}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="active">Активна</SelectItem>
                        <SelectItem value="inactive">Неактивно</SelectItem>
                        <SelectItem value="archived">В архиве</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm text-muted-foreground mb-1">Группа Keycloak</label>
                  <KeycloakGroupPicker
                    value={editGroupId}
                    onSelect={(group) => {
                      setEditGroupId(group?.id ?? null);
                      setEditGroupPath(group?.path ?? null);
                    }}
                  />
                  {editGroupPath && (
                    <p className="text-xs text-muted-foreground mt-1 font-mono">{editGroupPath}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm text-muted-foreground mb-1">Лимит участников</label>
                  <Input
                    type="number"
                    value={editMaxMembers}
                    onChange={(e) => setEditMaxMembers(e.target.value)}
                    placeholder="Без лимита"
                    className="max-w-[200px]"
                  />
                </div>
                <div className="flex gap-2 pt-2">
                  <Button size="sm" onClick={saveEdit} disabled={updateUnit.isPending}>
                    {updateUnit.isPending ? "Сохраняем…" : "Сохранить"}
                  </Button>
                  <Button variant="secondary" size="sm" onClick={() => setEditing(false)}>
                    Отмена
                  </Button>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <InfoItem label="Статус">
                  <Badge variant={unit.status === "active" ? "ok" : "neutral"}>
                    {unit.status}
                  </Badge>
                </InfoItem>
                <InfoItem label="Организация">
                  {linkedOrg ? (
                    <Link to={`/organizations/${linkedOrg.id}`} className="text-sm font-semibold text-primary hover:underline">
                      {linkedOrg.name}
                    </Link>
                  ) : (
                    <span className="text-sm text-muted-foreground">-</span>
                  )}
                </InfoItem>
                <InfoItem label="Участники">
                  <span className="text-sm font-semibold text-foreground">{unit.memberCount ?? members.length}</span>
                </InfoItem>
                <InfoItem label="Дочерние">
                  <span className="text-sm font-semibold text-foreground">{unit.childCount ?? unit.children?.length ?? 0}</span>
                </InfoItem>
                {unit.keycloakGroupPath && (
                  <InfoItem label="Путь группы">
                    <span className="text-xs text-muted-foreground font-mono truncate max-w-[180px] block">
                      {unit.keycloakGroupPath}
                    </span>
                  </InfoItem>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs defaultValue="members">
          <TabsList>
            <TabsTrigger value="members" className="gap-1.5">
              <Users className="h-3.5 w-3.5" />
              Members ({members.length})
            </TabsTrigger>
            <TabsTrigger value="bindings" className="gap-1.5">
              <Layers className="h-3.5 w-3.5" />
              Привязки приложений ({appBindings.length})
            </TabsTrigger>
            <TabsTrigger value="children" className="gap-1.5">
              <Building2 className="h-3.5 w-3.5" />
              Children ({unit.children?.length ?? 0})
            </TabsTrigger>
          </TabsList>

          {/* Members Tab */}
          <TabsContent value="members">
            <Card>
              <CardHeader className="flex-row items-center justify-between space-y-0 pb-3">
                <CardTitle className="text-sm font-medium text-muted-foreground uppercase">
                  Участники
                </CardTitle>
                {isPlatformAdmin && (
                  <Button size="sm" onClick={() => setShowAddMember(true)}>
                    <UserPlus className="h-3.5 w-3.5" /> Добавить участника
                  </Button>
                )}
              </CardHeader>
              <CardContent>
                {membersLoading ? (
                  <div className="space-y-2">
                    {Array.from({ length: 3 }).map((_, i) => (
                      <Skeleton key={i} className="h-12 w-full rounded-lg" />
                    ))}
                  </div>
                ) : members.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-8">
                    В подразделении пока нет участников.
                  </p>
                ) : (
                  <div className="space-y-2">
                    {members.map((member) => {
                      const displayName =
                        member.firstName || member.lastName
                          ? `${member.firstName ?? ""} ${member.lastName ?? ""}`.trim()
                          : member.username;
                      const initial = (member.firstName?.[0] ?? member.username[0] ?? "?").toUpperCase();

                      return (
                        <div
                          key={member.id}
                          className="flex items-center justify-between px-3 py-2.5 bg-muted/50 rounded-lg"
                        >
                          <div className="flex items-center gap-3">
                            <Avatar className="h-8 w-8">
                              <AvatarFallback className="text-xs">{initial}</AvatarFallback>
                            </Avatar>
                            <div>
                              <p className="text-sm text-foreground">{displayName}</p>
                              {member.email && (
                                <p className="text-xs text-muted-foreground">{member.email}</p>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge variant={member.enabled ? "ok" : "danger"} className="text-xs">
                              {member.enabled ? "Активен" : "Отключён"}
                            </Badge>
                            {isPlatformAdmin && (
                              <>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-7 w-7 text-muted-foreground hover:text-primary"
                                  onClick={() => setRolesForMember(member)}
                                  title="Роли приложений"
                                >
                                  <Shield className="h-3.5 w-3.5" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-7 w-7 text-muted-foreground hover:text-destructive"
                                  onClick={() => {
                                    removeMember.mutate(member.id, {
                                      onSuccess: () => toast.success("Участник удалён"),
                                      onError: () => toast.error("Не удалось удалить участника"),
                                    });
                                  }}
                                  title="Удалить участника"
                                >
                                  <X className="h-3.5 w-3.5" />
                                </Button>
                              </>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* App Bindings Tab */}
          <TabsContent value="bindings">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-muted-foreground uppercase">
                  Привязки ролей приложений
                </CardTitle>
              </CardHeader>
              <CardContent>
                {appBindings.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-8">
                    Для этого типа подразделения нет привязок приложений.
                  </p>
                ) : (
                  <div className="space-y-3">
                    {appBindings.map((binding) => (
                      <div
                        key={binding.name}
                        className="flex items-center justify-between px-3 py-2.5 bg-muted/50 rounded-lg"
                      >
                        <div>
                          <p className="text-sm font-medium text-foreground">{binding.clientId}</p>
                          <p className="text-xs text-muted-foreground font-mono">{binding.namespace}/{binding.name}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="flex flex-wrap gap-1 justify-end">
                            {binding.roles.map((role) => (
                              <RoleBadge key={role.name} name={role.name} description={role.description} />
                            ))}
                          </div>
                          <Badge variant={binding.status.ready ? "ok" : "neutral"} className="text-xs ml-2">
                            {binding.status.ready ? "Готово" : "Ожидание"}
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Children Tab */}
          <TabsContent value="children">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-muted-foreground uppercase">
                  Дочерние подразделения
                </CardTitle>
              </CardHeader>
              <CardContent>
                {!unit.children || unit.children.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-8">
                    Дочерних подразделений нет.
                  </p>
                ) : (
                  <div className="space-y-2">
                    {unit.children.map((child) => (
                      <ChildUnitRow
                        key={child.id}
                        unit={child}
                        onClick={() => navigate(`/org-structure/${child.id}`)}
                      />
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Delete confirmation dialog */}
        <Dialog open={showDelete} onOpenChange={setShowDelete}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Удалить подразделение</DialogTitle>
              <DialogDescription>
                Удалить <strong className="text-foreground">{unit.name}</strong>? Подразделение и его группа в Keycloak будут удалены.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="secondary" onClick={() => setShowDelete(false)}>
                Отмена
              </Button>
              <Button
                variant="danger"
                onClick={handleDelete}
                disabled={deleteUnit.isPending}
              >
                {deleteUnit.isPending ? "Удаляем…" : "Удалить"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Add member dialog */}
        {showAddMember && (
          <AddMemberDialog
            unitId={unitId}
            appBindings={appBindings}
            onClose={() => setShowAddMember(false)}
          />
        )}

        {/* Manage app roles dialog */}
        {rolesForMember && (
          <MemberRolesDialog
            unitId={unitId}
            member={rolesForMember}
            appBindings={appBindings}
            onClose={() => setRolesForMember(null)}
          />
        )}
      </div>
    </ScrollArea>
  );
}

function InfoItem({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="text-xs text-muted-foreground mb-1">{label}</p>
      {children}
    </div>
  );
}

function ChildUnitRow({ unit, onClick }: { unit: OrgUnit; onClick: () => void }) {
  return (
    <div
      className="flex items-center justify-between px-3 py-2.5 bg-muted/50 rounded-lg cursor-pointer hover:bg-muted transition-colors"
      onClick={onClick}
    >
      <div className="flex items-center gap-3">
        <OrgUnitTypeBadge type={unit.type} />
        <div>
          <p className="text-sm font-medium text-foreground">{unit.name}</p>
          <p className="text-xs text-muted-foreground font-mono">{unit.slug}</p>
        </div>
      </div>
      <div className="flex items-center gap-3">
        {unit.memberCount != null && (
          <span className="text-xs text-muted-foreground">
            {unit.memberCount} member{unit.memberCount !== 1 ? "s" : ""}
          </span>
        )}
        <Badge variant={unit.status === "active" ? "ok" : "neutral"} className="text-xs">
          {unit.status}
        </Badge>
      </div>
    </div>
  );
}

function AddMemberDialog({
  unitId,
  appBindings,
  onClose,
}: {
  unitId: string;
  appBindings: AppRoleBinding[];
  onClose: () => void;
}) {
  const [userId, setUserId] = useState<string | null>(null);
  const [selectedRoles, setSelectedRoles] = useState<Record<string, string[]>>({});
  const addMember = useAddOrgUnitMember(unitId);
  const assignRoles = useAssignMemberAppRoles(unitId);
  const [submitting, setSubmitting] = useState(false);

  function toggleRole(clientId: string, roleName: string) {
    setSelectedRoles((prev) => {
      const current = prev[clientId] ?? [];
      const has = current.includes(roleName);
      return {
        ...prev,
        [clientId]: has ? current.filter((r) => r !== roleName) : [...current, roleName],
      };
    });
  }

  async function handleAdd() {
    if (!userId) return;
    setSubmitting(true);
    try {
      // Step 1: Add to group
      await addMember.mutateAsync(userId);

      // Step 2: Assign selected roles per app
      for (const [clientId, roles] of Object.entries(selectedRoles)) {
        if (roles.length > 0) {
          await assignRoles.mutateAsync({ userId, clientId, roles });
        }
      }

      toast.success("Участник добавлен с ролями");
      onClose();
    } catch (err) {
      toast.error(`Не удалось добавить участника: ${err instanceof Error ? err.message : "неизвестная ошибка"}`);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Добавить участника команды</DialogTitle>
          <DialogDescription>Найдите пользователя и назначьте ему роли приложений.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <label className="block text-sm text-muted-foreground mb-1">Пользователь</label>
            <UserPicker value={userId} onChange={setUserId} placeholder="Поиск пользователей…" />
          </div>

          {userId && appBindings.length > 0 && (
            <div>
              <label className="block text-sm text-muted-foreground mb-2">Роли приложений (необязательно)</label>
              <div className="space-y-3">
                {appBindings.map((binding) => (
                  <div key={binding.clientId}>
                    <p className="text-xs font-medium text-foreground mb-1.5">{binding.clientId}</p>
                    <div className="flex flex-wrap gap-1.5">
                      {binding.roles.map((role) => {
                        const active = (selectedRoles[binding.clientId] ?? []).includes(role.name);
                        return (
                          <Button
                            key={role.name}
                            type="button"
                            size="sm"
                            variant={active ? "primary" : "outline"}
                            className="text-xs h-7"
                            onClick={() => toggleRole(binding.clientId, role.name)}
                            title={role.description}
                          >
                            {role.name}
                          </Button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button type="button" variant="secondary" onClick={onClose}>
            Отмена
          </Button>
          <Button onClick={handleAdd} disabled={!userId || submitting}>
            {submitting ? "Добавляем…" : "Добавить участника"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
